import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { api } from "../../services/api/api";
import toast from "react-hot-toast";

// Cache global pour les URLs d'images
const imageCache = new Map<string, string>();

interface LazyImageProps {
  workorderId: string;
  photoId: string;
  alt: string;
  className?: string;
  onLoad?: (url: string) => void;
  src?: string;
  onClick?: () => void;
  type: "BEFORE" | "AFTER";
  getDownloadUrl: (photoId: string) => string;
  onError?: () => void;
}

export const LazyImage = ({
  workorderId,
  photoId,
  alt,
  className,
  onLoad,
  src,
  onClick,
  getDownloadUrl,
  onError,
}: LazyImageProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const currentImageUrl = useRef<string | null>(null);
  const isMounted = useRef(true);
  const isLoadingRef = useRef(isLoading);

  // Mettre à jour la ref quand l'état change
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Créer une clé de cache unique pour cette image
  const cacheKey = useMemo(() =>
    `${workorderId}_${photoId}`,
  [workorderId, photoId]);

  // Fonction pour nettoyer le cache
  const clearCache = useCallback(() => {
    if (currentImageUrl.current) {
      URL.revokeObjectURL(currentImageUrl.current);
      currentImageUrl.current = null;
    }
    imageCache.delete(cacheKey);
  }, [cacheKey]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      clearCache();
    };
  }, [clearCache]);

  useEffect(() => {
    const loadImage = async () => {
      if (isLoadingRef.current) return;

      setIsLoading(true);
      setError(null);

      try {
        // Si une source est fournie directement, l'utiliser
        if (src) {
          setImageUrl(src);
          if (onLoad) onLoad(src);
          setIsLoading(false);
          return;
        }

        // Vérifier le cache
        const cachedUrl = imageCache.get(cacheKey);
        if (cachedUrl) {
          setImageUrl(cachedUrl);
          if (onLoad) onLoad(cachedUrl);
          setIsLoading(false);
          return;
        }

        // Nettoyer l'ancienne URL si elle existe
        clearCache();

        // Charger la nouvelle image
        const url = getDownloadUrl(photoId);
        const response = await api.getPhotos(url);

        // Détecter le type MIME de l'image depuis les headers de la réponse
        let mimeType = "image/jpeg"; // Valeur par défaut
        if (response && typeof response === "object" && "headers" in response) {
          const contentType = (response as any).headers?.["content-type"] ||
            (response as any).headers?.["Content-Type"];
          if (contentType && contentType.includes("image/")) {
            mimeType = contentType;
          }
        }

        const blob = new Blob([response as ArrayBuffer], { type: mimeType });
        const newImageUrl = URL.createObjectURL(blob);

        if (isMounted.current) {
          currentImageUrl.current = newImageUrl;
          setImageUrl(newImageUrl);
          imageCache.set(cacheKey, newImageUrl);
          if (onLoad) onLoad(newImageUrl);
        }
      } catch (err) {
        if (isMounted.current) {
          console.error("Erreur lors du chargement de l'image:", err);
          setError(err instanceof Error ? err.message : "Failed to load image");
          if (onError) onError();
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    loadImage();
  }, [workorderId, photoId, src, cacheKey, getDownloadUrl, onLoad, onError, clearCache]);

  if (error) {
    toast.error("Impossible de charger l'image. Veuillez réessayer plus tard.");
    return null;
  }

  return imageUrl ? (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onClick={onClick}
      style={{ width: "100%", height: "100%" }}
    />
  ) : (
    <div className="animate-pulse bg-gray-200 w-full h-full" />
  );
};